/**
 * The COMPONENT glyphs (DESIGN_SYSTEM.md §5.1, QD10): icons that are a whole drawing (several paths, circles,
 * clip paths, or shapes made of Views), which one path cannot hold. The POS's only; the kiosk's stay in
 * `packages/ui/assets` (§3.4).
 *
 * Each takes `{ size, color }`: `size` is its HEIGHT (a glyph that is not square keeps its own width from its
 * aspect), `color` its ink. Their old homes (`packages/ui/assets/*`, the loyalty and customer-summary
 * components) are thin wrappers that pass the size and colour they always drew, so every live surface draws
 * exactly what it did.
 *
 * No colour is written here (check R): a glyph's second colour is a role whose value it already was (white is
 * `surface.raised` / `text.inverse`), or the caller's `color`.
 */
import React from "react";
import { StyleSheet, View } from "react-native";
import Svg, { Circle, ClipPath, Defs, G, Path, Rect } from "react-native-svg";

import { useTheme } from "@rista/ui/library/theme/ThemeProvider";

export interface GlyphProps {
  /** the glyph's height */
  size: number;
  color: string;
}

const box = StyleSheet.create({ centre: { alignItems: "center", justifyContent: "center" } });

// ── drawn with Views (PhoneCountryDropdown's marks, from `packages/ui/assets/*.jsx`) ────────────────────────

export function ChevronDownLine({ size, color }: GlyphProps) {
  return (
    <View style={[box.centre, { width: size, height: size }]}>
      <View
        style={{
          width: size * 0.45,
          height: size * 0.45,
          // rtl-exempt: the glyph's own geometry; a caret that pointed down would point sideways mirrored
          borderRightWidth: 1.5,
          borderBottomWidth: 1.5,
          borderColor: color,
          transform: [{ rotate: "45deg" }, { translateY: -size * 0.1 }],
        }}
      />
    </View>
  );
}

export function SearchLine({ size, color }: GlyphProps) {
  return (
    <View style={[box.centre, { width: size, height: size }]}>
      <View
        style={{
          width: size * 0.62,
          height: size * 0.62,
          borderRadius: size * 0.31,
          borderWidth: 1.5,
          borderColor: color,
          transform: [{ translateX: -size * 0.08 }, { translateY: -size * 0.08 }],
        }}
      />
      <View
        style={{
          position: "absolute",
          width: size * 0.34,
          height: 1.5,
          backgroundColor: color,
          // rtl-exempt: the handle's place on the lens is the drawing's, not the reading direction's
          right: size * 0.02,
          bottom: size * 0.2,
          transform: [{ rotate: "45deg" }],
        }}
      />
    </View>
  );
}

export function CloseLine({ size, color }: GlyphProps) {
  const bar = { position: "absolute" as const, width: size * 0.8, height: 1.5, backgroundColor: color };
  return (
    <View style={[box.centre, { width: size, height: size }]}>
      <View style={[bar, { transform: [{ rotate: "45deg" }] }]} />
      <View style={[bar, { transform: [{ rotate: "-45deg" }] }]} />
    </View>
  );
}

export function MobilePhone({ size, color }: GlyphProps) {
  return (
    <View style={[box.centre, { width: size, height: size }]}>
      <View
        style={{
          width: size * 0.56,
          height: size * 0.9,
          borderWidth: 1.5,
          borderColor: color,
          borderRadius: size * 0.12,
          alignItems: "center",
          justifyContent: "flex-end",
          paddingBottom: size * 0.1,
        }}
      >
        <View
          style={{
            width: Math.max(2, size * 0.1),
            height: Math.max(2, size * 0.1),
            borderRadius: size * 0.05,
            backgroundColor: color,
          }}
        />
      </View>
    </View>
  );
}

/** a desk telephone: the handset as an arch over a rounded base with a dial */
export function LandlinePhone({ size, color }: GlyphProps) {
  return (
    <View style={[box.centre, { width: size, height: size }]}>
      <View
        style={{
          width: size * 0.92,
          height: size * 0.3,
          borderWidth: 1.5,
          borderBottomWidth: 0,
          borderColor: color,
          // rtl-exempt: an arch is symmetrical; these two corners are the drawing's top
          borderTopLeftRadius: size * 0.3,
          borderTopRightRadius: size * 0.3,
        }}
      />
      <View
        style={{
          width: size * 0.66,
          height: size * 0.5,
          marginTop: -size * 0.04,
          borderWidth: 1.5,
          borderColor: color,
          borderRadius: size * 0.1,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <View style={{ width: size * 0.22, height: size * 0.22, borderRadius: size * 0.11, borderWidth: 1.5, borderColor: color }} />
      </View>
    </View>
  );
}

// ── outlined SVG drawings (the customer profile card's, the customer display's eye) ────────────────────────

export function Eye({ size, color }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M1.5 12c1.9-3.3 5.4-7 10.5-7s8.6 3.7 10.5 7c-1.9 3.3-5.4 7-10.5 7s-8.6-3.7-10.5-7z"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Circle cx={12} cy={12} r={2.75} stroke={color} strokeWidth={1.8} />
    </Svg>
  );
}

export function Lock({ size, color }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <Path
        d="M9.5 5.5H2.5C1.94772 5.5 1.5 5.94772 1.5 6.5V10C1.5 10.5523 1.94772 11 2.5 11H9.5C10.0523 11 10.5 10.5523 10.5 10V6.5C10.5 5.94772 10.0523 5.5 9.5 5.5Z"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M3.5 5.5V3.5C3.5 2.83696 3.76339 2.20107 4.23223 1.73223C4.70107 1.26339 5.33696 1 6 1C6.66304 1 7.29893 1.26339 7.76777 1.73223C8.23661 2.20107 8.5 2.83696 8.5 3.5V5.5"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** a stroked tick, 12 × 9 (Figma g7uVGZNqGcFZf1vcftGorL, node 272:316) */
export function CheckMark({ size, color }: GlyphProps) {
  return (
    <Svg width={(size * 12) / 9} height={size} viewBox="0 0 12 9" fill="none">
      <Path d="M1 4L4.5 7.5L11 1" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** a head-and-shoulders outline (Figma g7uVGZNqGcFZf1vcftGorL, node 125:269) */
export function Person({ size, color }: GlyphProps) {
  const line = { stroke: color, strokeWidth: 1.16667, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return (
    <Svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <Path
        d="M11.0837 12.25V11.0833C11.0837 10.4645 10.8378 9.871 10.4002 9.43342C9.96266 8.99583 9.36916 8.75 8.75033 8.75H5.25033C4.63149 8.75 4.03799 8.99583 3.60041 9.43342C3.16282 9.871 2.91699 10.4645 2.91699 11.0833V12.25"
        {...line}
      />
      <Path
        d="M7.00033 6.41667C8.28899 6.41667 9.33366 5.372 9.33366 4.08333C9.33366 2.79467 8.28899 1.75 7.00033 1.75C5.71166 1.75 4.66699 2.79467 4.66699 4.08333C4.66699 5.372 5.71166 6.41667 7.00033 6.41667Z"
        {...line}
      />
    </Svg>
  );
}

// ── the loyalty surfaces' (customer summary, redemption) ────────────────────────────────────────────────────

/** a price tag: a rotated rounded square with an eyelet, painted in the raised surface (there is no mask in RN) */
export function Tag({ size, color }: GlyphProps) {
  const hole = Math.max(2, Math.round(size / 5));
  const surface = useTheme().color.surface.raised;
  return (
    <View
      style={{
        width: size,
        height: size,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: color,
        borderRadius: Math.max(2, Math.round(size / 6)),
        transform: [{ rotate: "45deg" }],
      }}
      accessibilityElementsHidden
      importantForAccessibility="no"
    >
      {/* rtl-exempt: the eyelet sits in the drawing's corner, turned with it */}
      <View style={{ position: "absolute", top: hole, left: hole, width: hole, height: hole, borderRadius: hole, backgroundColor: surface }} />
    </View>
  );
}

/** a circled lower-case i, drawn as two marks so it does not depend on a font */
export function Info({ size, color }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <Circle cx={8} cy={8} r={7} stroke={color} strokeWidth={1.4} />
      <Circle cx={8} cy={4.6} r={0.9} fill={color} />
      <Rect x={7.2} y={6.6} width={1.6} height={5} rx={0.8} fill={color} />
    </Svg>
  );
}

/** a gift box with two sparkles on a disc: the disc is `color`, the drawing on it the inverse ink */
export function UnclaimedRewards({ size, color }: GlyphProps) {
  const ink = useTheme().color.text.inverse;
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={12} fill={color} />
      <Defs>
        <ClipPath id="unclaimed-sparkle-lg">
          <Path fill={ink} d="M16 4h4v4h-4z" />
        </ClipPath>
        <ClipPath id="unclaimed-sparkle-sm">
          <Path fill={ink} d="M13 3h3v3h-3z" />
        </ClipPath>
      </Defs>
      <G clipPath="url(#unclaimed-sparkle-lg)">
        <Path
          fill={ink}
          d="M17.882 4.32c.04-.109.195-.109.235 0l.12.327a1.876 1.876 0 0 0 1.116 1.116l.326.12c.11.04.11.194 0 .234l-.326.12a1.874 1.874 0 0 0-1.117 1.116l-.12.326a.125.125 0 0 1-.234 0l-.12-.326a1.876 1.876 0 0 0-1.115-1.117l-.327-.119a.125.125 0 0 1 0-.235l.327-.12a1.875 1.875 0 0 0 1.116-1.115l.12-.327Z"
        />
      </G>
      <G clipPath="url(#unclaimed-sparkle-sm)">
        <Path
          fill={ink}
          d="M14.413 3.241a.094.094 0 0 1 .176 0l.09.245a1.406 1.406 0 0 0 .836.837l.245.09a.094.094 0 0 1 0 .176l-.245.09a1.406 1.406 0 0 0-.837.836l-.09.245a.094.094 0 0 1-.175 0l-.09-.245a1.406 1.406 0 0 0-.837-.837l-.245-.09a.094.094 0 0 1 0-.175l.245-.09a1.407 1.407 0 0 0 .837-.837l.09-.245Z"
        />
      </G>
      <Path
        stroke={ink}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={0.9}
        d="M16.802 10.201h-9.6a.6.6 0 0 0-.6.6v1.2a.6.6 0 0 0 .6.6h9.6a.6.6 0 0 0 .6-.6v-1.2a.6.6 0 0 0-.6-.6ZM12 10.2V18"
      />
      <Path
        stroke={ink}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={0.9}
        d="M16.197 12.6v4.2a1.2 1.2 0 0 1-1.2 1.2h-6a1.2 1.2 0 0 1-1.2-1.2v-4.2M9.297 10.201a1.5 1.5 0 1 1 0-3c.579-.01 1.146.271 1.627.806.482.535.856 1.3 1.073 2.194.217-.894.59-1.659 1.072-2.194.482-.535 1.05-.816 1.628-.806a1.5 1.5 0 0 1 0 3"
      />
    </Svg>
  );
}

/** the registry: a name an `Icon` takes → its drawing */
export const COMPONENT_ICONS = {
  chevronDownLine: ChevronDownLine,
  searchLine: SearchLine,
  closeLine: CloseLine,
  mobilePhone: MobilePhone,
  landlinePhone: LandlinePhone,
  eye: Eye,
  lock: Lock,
  checkMark: CheckMark,
  person: Person,
  tag: Tag,
  info: Info,
  unclaimedRewards: UnclaimedRewards,
} as const satisfies Record<string, (props: GlyphProps) => React.ReactElement>;

export type ComponentIconName = keyof typeof COMPONENT_ICONS;
